import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Star, MessageCircle, Users, Trophy, Lock, ChevronUp, Gift, Crown } from 'lucide-react';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';
import { GemIcon } from '../components/GemIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  0: '#71717A',
  1: '#A1A1AA',
  2: '#60A5FA',
  3: '#34D399',
  4: '#FBBF24',
  5: '#F59E0B',
  6: '#F97316',
  7: '#EF4444',
  8: '#E11D48',
  9: '#A855F7',
  10: '#E11D48',
};

const LEVEL_GRADIENTS = {
  0: 'from-[#71717A]/20 to-[#71717A]/5',
  1: 'from-[#A1A1AA]/20 to-[#A1A1AA]/5',
  2: 'from-[#60A5FA]/20 to-[#60A5FA]/5',
  3: 'from-[#34D399]/20 to-[#34D399]/5',
  4: 'from-[#FBBF24]/20 to-[#FBBF24]/5',
  5: 'from-[#F59E0B]/20 to-[#F59E0B]/5',
  6: 'from-[#F97316]/20 to-[#F97316]/5',
  7: 'from-[#EF4444]/20 to-[#EF4444]/5',
  8: 'from-[#E11D48]/20 to-[#E11D48]/5',
  9: 'from-[#A855F7]/20 to-[#A855F7]/5',
  10: 'from-[#E11D48]/30 via-[#F59E0B]/20 to-[#E11D48]/5',
};

export default function CreatorRewards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showGemAlert, setShowGemAlert] = useState(false);
  const [gemsAwarded, setGemsAwarded] = useState(0);

  useEffect(() => {
    if (user) fetchAll();
  }, [user]);

  const fetchAll = async () => {
    try {
      const [statsRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/creator/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/creator/leaderboard`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setLeaderboard(lbRes.data);
      if (statsRes.data.gems_just_awarded > 0) {
        setGemsAwarded(statsRes.data.gems_just_awarded);
        setShowGemAlert(true);
        setTimeout(() => setShowGemAlert(false), 5000);
      }
    } catch (err) {
      console.error('Error fetching creator stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const levelColor = stats ? LEVEL_COLORS[stats.level] || '#E11D48' : '#71717A';
  const progressPct = stats ? Math.min((stats.progress_current / stats.progress_max) * 100, 100) : 0;

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-12 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="rewards-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Gift className="w-5 h-5 text-[#F59E0B]" />
        <h1 className="font-bold text-white font-['Unbounded']">Creator Rewards</h1>
      </div>

      {/* Gem Award Alert */}
      {showGemAlert && (
        <div className="mx-4 mt-3 p-3 bg-gradient-to-r from-[#F59E0B]/20 to-[#E11D48]/20 border border-[#F59E0B]/40 rounded-xl flex items-center gap-3 animate-in slide-in-from-top" data-testid="gem-award-alert">
          <GemIcon size={24} />
          <div>
            <p className="text-sm font-bold text-[#F59E0B]">Level Up Reward!</p>
            <p className="text-xs text-[#A1A1AA]">You earned <span className="text-[#F59E0B] font-bold">{gemsAwarded}</span> gems for your creator achievements!</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : stats && (
        <>
          {/* Level Hero Card */}
          <div className={`mx-4 mt-4 p-5 rounded-2xl bg-gradient-to-br ${LEVEL_GRADIENTS[stats.level]} border border-white/10 relative overflow-hidden`} data-testid="level-hero-card">
            {/* Decorative */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: levelColor, filter: 'blur(40px)' }} />

            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full flex items-center justify-center border-2" style={{ borderColor: levelColor, background: `${levelColor}15` }}>
                  <span className="text-2xl font-black font-['Unbounded']" style={{ color: levelColor }}>{stats.level}</span>
                </div>
                {stats.level === 10 && (
                  <Crown className="absolute -top-2 -right-1 w-5 h-5 text-[#F59E0B]" />
                )}
              </div>
              <div>
                <p className="text-xs text-[#71717A] uppercase tracking-wider">Creator Level</p>
                <h2 className="text-xl font-bold font-['Unbounded']" style={{ color: levelColor }} data-testid="level-name">{stats.level_name}</h2>
              </div>
            </div>

            {/* Progress Bar */}
            {stats.level < 10 ? (
              <div>
                <div className="flex justify-between text-xs text-[#A1A1AA] mb-1.5">
                  <span>Progress to Level {stats.level + 1}</span>
                  <span>{stats.total_chats} / {stats.next_level_chats} chats</span>
                </div>
                <div className="h-2.5 bg-white/5 rounded-full overflow-hidden" data-testid="level-progress-bar">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${levelColor}, ${levelColor}aa)` }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#F59E0B] font-medium">Max level reached! You are a Legend.</p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 mx-4 mt-3">
            <div className="p-3 bg-[#1A1D24] rounded-xl border border-white/5 text-center" data-testid="stat-total-chats">
              <MessageCircle className="w-4 h-4 text-[#E11D48] mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.total_chats.toLocaleString()}</p>
              <p className="text-[10px] text-[#71717A]">Total Chats</p>
            </div>
            <div className="p-3 bg-[#1A1D24] rounded-xl border border-white/5 text-center" data-testid="stat-avg-rating">
              <Star className="w-4 h-4 text-[#F59E0B] mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.avg_rating > 0 ? stats.avg_rating : '--'}</p>
              <p className="text-[10px] text-[#71717A]">Avg Rating</p>
            </div>
            <div className="p-3 bg-[#1A1D24] rounded-xl border border-white/5 text-center" data-testid="stat-gems-earned">
              <GemIcon size={16} />
              <p className="text-lg font-bold text-white">{stats.total_gems_earned.toLocaleString()}</p>
              <p className="text-[10px] text-[#71717A]">Gems Earned</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10 mx-4 mt-5">
            {['overview', 'characters', 'leaderboard'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-sm font-medium transition-colors relative capitalize ${activeTab === tab ? 'text-white' : 'text-[#71717A]'}`}
                data-testid={`tab-${tab}`}
              >
                {tab}
                {activeTab === tab && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="px-4 py-4">
            {activeTab === 'overview' && (
              <div className="space-y-2">
                <h3 className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-3">Level Milestones</h3>
                {stats.levels.map((lvl) => (
                  <div
                    key={lvl.level}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      lvl.unlocked
                        ? 'bg-[#1A1D24] border-white/10'
                        : 'bg-[#0F1115] border-white/5 opacity-60'
                    }`}
                    data-testid={`milestone-level-${lvl.level}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{
                        borderColor: lvl.unlocked ? LEVEL_COLORS[lvl.level] : '#333',
                        background: lvl.unlocked ? `${LEVEL_COLORS[lvl.level]}20` : '#1a1a1a',
                      }}
                    >
                      {lvl.unlocked ? (
                        <span className="text-sm font-bold" style={{ color: LEVEL_COLORS[lvl.level] }}>{lvl.level}</span>
                      ) : (
                        <Lock className="w-4 h-4 text-[#555]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{lvl.name}</span>
                        {lvl.unlocked && <ChevronUp className="w-3 h-3 text-green-500" />}
                      </div>
                      <p className="text-xs text-[#71717A]">{lvl.chats_required.toLocaleString()} total chats required</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <GemIcon size={12} />
                      <span className={`text-sm font-bold ${lvl.unlocked ? 'text-[#F59E0B]' : 'text-[#555]'}`}>
                        +{lvl.gems_reward}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'characters' && (
              <div className="space-y-2">
                {stats.characters.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
                    <p className="text-[#71717A] mb-1">No characters yet</p>
                    <p className="text-xs text-[#555]">Create characters to start earning rewards</p>
                  </div>
                ) : (
                  stats.characters.map((char) => (
                    <div
                      key={char.id}
                      className="flex items-center gap-3 p-3 bg-[#1A1D24] rounded-xl border border-white/5 cursor-pointer hover:bg-[#1A1D24]/80 transition-colors"
                      onClick={() => navigate(`/character/${char.id}`)}
                      data-testid={`char-breakdown-${char.id}`}
                    >
                      <img
                        src={char.avatar_url || fallbackAvatarUrl(char.name)}
                        alt={char.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => { e.target.src = fallbackAvatarUrl(char.name); }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{char.name}</h4>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-[#71717A] flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" /> {char.chat_count.toLocaleString()}
                          </span>
                          {char.review_count > 0 && (
                            <span className="text-xs text-[#71717A] flex items-center gap-1">
                              <Star className="w-3 h-3 text-[#F59E0B]" /> {char.avg_rating}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-center text-[#71717A] py-12">No creators ranked yet</p>
                ) : (
                  leaderboard.map((creator) => {
                    const isMe = creator.creator_id === user?.id;
                    const rankColors = { 1: '#F59E0B', 2: '#A1A1AA', 3: '#CD7F32' };
                    return (
                      <div
                        key={creator.creator_id}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          isMe ? 'bg-[#E11D48]/10 border-[#E11D48]/30' : 'bg-[#1A1D24] border-white/5'
                        }`}
                        data-testid={`leaderboard-rank-${creator.rank}`}
                      >
                        {/* Rank */}
                        <div className="w-8 text-center flex-shrink-0">
                          {creator.rank <= 3 ? (
                            <Trophy className="w-5 h-5 mx-auto" style={{ color: rankColors[creator.rank] }} />
                          ) : (
                            <span className="text-sm font-bold text-[#71717A]">#{creator.rank}</span>
                          )}
                        </div>
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-[#0F1115] flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/10">
                          {creator.avatar ? (
                            <img src={creator.avatar} alt={creator.name} className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-5 h-5 text-[#71717A]" />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white truncate">{creator.name}</span>
                            {creator.badge && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#E11D48] text-white rounded-full">
                                <Crown className="w-2.5 h-2.5" /> {creator.badge}
                              </span>
                            )}
                            {isMe && <span className="text-[10px] text-[#E11D48] font-bold">YOU</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{
                              color: LEVEL_COLORS[creator.level],
                              background: `${LEVEL_COLORS[creator.level]}15`,
                            }}>
                              Lv.{creator.level} {creator.level_name}
                            </span>
                          </div>
                        </div>
                        {/* Stats */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-white">{creator.total_chats.toLocaleString()}</p>
                          <p className="text-[10px] text-[#71717A]">chats</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
